import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Plus, CheckCircle, Trash2, Calendar, Upload, Eye, Download, Pencil } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { formatCurrency } from '../utils/currency';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [projectEditDialogOpen, setProjectEditDialogOpen] = useState(false);
  const [teamMemberEditDialogOpen, setTeamMemberEditDialogOpen] = useState(false);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [deleteTeamMemberDialogOpen, setDeleteTeamMemberDialogOpen] = useState(false);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [editingTeamMemberIndex, setEditingTeamMemberIndex] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [teamMemberToDelete, setTeamMemberToDelete] = useState(null);
  
  const [taskForm, setTaskForm] = useState({
    description: '',
    deadline: '',
    picName: '',
    assignedTo: [] // Now: [{name, subDescription, deadline}]
  });
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [personDeadlines, setPersonDeadlines] = useState({}); // {name: [{date, project, task}]}

  const [newTeamMember, setNewTeamMember] = useState({
    name: '',
    role: '',
    contractValue: 0,
    initialPaid: 0,
    contractDescription: '',
    assignmentDate: '',
    contractFile: null,
    contractFileName: '',
    subWorkIds: []
  });

  const [projectEditForm, setProjectEditForm] = useState({
    contractValue: 0,
    initialPaid: 0,
    clientId: '',
    clientName: '',
    subWorks: [],
    financeBook: 'freelance'
  });

  const [teamMemberEditForm, setTeamMemberEditForm] = useState({
    contractValue: 0,
    initialPaid: 0,
    contractDescription: '',
    assignmentDate: '',
    subWorkIds: []
  });

  const milestones = ['Schematic', 'DED', 'For Construction', 'Done'];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, tasksRes, teamRes, clientsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/tasks?projectId=${id}`),
        axios.get(`${API}/team`),
        axios.get(`${API}/clients`)
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setAllTeamMembers(teamRes.data);
      setAllClients(clientsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project details');
      setLoading(false);
    }
  };

  const addPersonToTask = (personName) => {
    if (taskForm.assignedTo.length >= 5) {
      toast.error('Maximum 5 people can be assigned to a task');
      return;
    }
    if (taskForm.assignedTo.find(p => p.name === personName)) {
      toast.error('Person already added');
      return;
    }
    setTaskForm({
      ...taskForm,
      assignedTo: [...taskForm.assignedTo, { name: personName, subDescription: '', deadline: '' }],
      picName: taskForm.picName || personName
    });
    // Fetch this person's existing deadlines
    fetchPersonDeadlines(personName);
  };

  const fetchPersonDeadlines = async (personName) => {
    try {
      const member = allTeamMembers.find(m => m.name === personName);
      if (!member) return;
      const res = await axios.get(`${API}/team/${member.id}/pending-tasks`);
      setPersonDeadlines(prev => ({
        ...prev,
        [personName]: res.data.map(t => ({
          date: t.personDeadline || t.mainDeadline,
          project: t.projectName,
          task: t.personSubDescription || t.taskDescription
        })).filter(d => d.date)
      }));
    } catch { /* ignore */ }
  };

  const removePersonFromTask = (personName) => {
    const updated = taskForm.assignedTo.filter(p => p.name !== personName);
    setTaskForm({
      ...taskForm,
      assignedTo: updated,
      picName: updated[0]?.name || ''
    });
  };

  const updatePersonInTask = (index, field, value) => {
    const updated = [...taskForm.assignedTo];
    updated[index] = { ...updated[index], [field]: value };
    setTaskForm({ ...taskForm, assignedTo: updated });
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    
    if (!taskForm.description || !taskForm.deadline || taskForm.assignedTo.length === 0) {
      toast.error('Please fill in all fields and assign at least one person');
      return;
    }

    // Validate per-person fields
    const incomplete = taskForm.assignedTo.find(p => !p.subDescription || !p.deadline);
    if (incomplete) {
      toast.error(`Please fill in job description and deadline for ${incomplete.name}`);
      return;
    }

    try {
      await axios.post(`${API}/tasks`, {
        projectId: id,
        description: taskForm.description,
        deadline: taskForm.deadline,
        picName: taskForm.assignedTo[0].name,
        assignedTo: taskForm.assignedTo
      });
      
      toast.success('Task created successfully!');
      setDialogOpen(false);
      setTaskForm({ description: '', deadline: '', picName: '', assignedTo: [] });
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const openEditTask = (task) => {
    setEditingTask({
      ...task,
      assignedTo: (task.assignedTo || []).map(p => ({
        name: p.name || p,
        subDescription: p.subDescription || '',
        deadline: p.deadline || ''
      }))
    });
    setEditTaskDialogOpen(true);
  };

  const handleEditTaskSubmit = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, {
        description: editingTask.description,
        deadline: editingTask.deadline,
        assignedTo: editingTask.assignedTo
      });
      toast.success('Task updated!');
      setEditTaskDialogOpen(false);
      setEditingTask(null);
      fetchData();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const togglePersonCompletion = async (taskId, personName) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/toggle-person/${encodeURIComponent(personName)}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling person completion:', error);
      toast.error('Failed to update task');
    }
  };

  const toggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'pending' ? 'completed' : 'pending';
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success(`Task marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTaskClick = (taskId) => {
    setTaskToDelete(taskId);
    setDeleteTaskDialogOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await axios.delete(`${API}/tasks/${taskToDelete}`);
      toast.success('✅ Successfully deleted');
      setDeleteTaskDialogOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('⚠️ Deletion failed. Please try again later');
      setDeleteTaskDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleDeleteTeamMemberClick = async (index) => {
    const member = project.teamMembers[index];
    
    // Check if team member has any transactions
    try {
      const transactionsRes = await axios.get(`${API}/transactions`);
      const hasTransactions = transactionsRes.data.some(
        t => t.teamMemberName === member.name && t.projectId === id
      );
      
      if (hasTransactions) {
        toast.error("Team Member Can't be Removed. Please review the transaction");
        return;
      }
      
      setTeamMemberToDelete(index);
      setDeleteTeamMemberDialogOpen(true);
    } catch (error) {
      console.error('Error checking transactions:', error);
      toast.error('Failed to check transactions');
    }
  };

  const confirmDeleteTeamMember = async () => {
    if (teamMemberToDelete === null) return;

    try {
      const updatedTeamMembers = project.teamMembers.filter((_, i) => i !== teamMemberToDelete);
      
      await axios.put(`${API}/projects/${id}`, {
        teamMembers: updatedTeamMembers
      });
      
      toast.success('✅ Team member removed successfully');
      setDeleteTeamMemberDialogOpen(false);
      setTeamMemberToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error removing team member:', error);
      toast.error('⚠️ Failed to remove team member');
      setDeleteTeamMemberDialogOpen(false);
      setTeamMemberToDelete(null);
    }
  };

  const updateMilestone = async (newMilestone) => {
    try {
      await axios.put(`${API}/projects/${id}`, { milestone: newMilestone });
      toast.success('Milestone updated successfully');
      setMilestoneDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast.error('Failed to update milestone');
    }
  };

  const handleAddTeamMember = async (e) => {
    e.preventDefault();
    
    const isPrincipalEngineer = newTeamMember.role === 'Principal Engineer';
    if (!newTeamMember.name || (!isPrincipalEngineer && !newTeamMember.contractValue)) {
      toast.error(isPrincipalEngineer ? 'Please select a team member' : 'Please select a team member and enter contract value');
      return;
    }

    try {
      // Get current team members and add new one
      const updatedTeamMembers = [
        ...(project.teamMembers || []),
        {
          name: newTeamMember.name,
          role: newTeamMember.role,
          contractValue: isPrincipalEngineer ? 0 : parseFloat(newTeamMember.contractValue),
          initialPaid: isPrincipalEngineer ? 0 : (parseFloat(newTeamMember.initialPaid) || 0),
          amountPaid: 0,
          contractDescription: newTeamMember.contractDescription || '',
          assignmentDate: newTeamMember.assignmentDate || '',
          contractFile: newTeamMember.contractFile || null,
          contractFileName: newTeamMember.contractFileName || '',
          subWorkIds: newTeamMember.subWorkIds || []
        }
      ];

      await axios.put(`${API}/projects/${id}`, { 
        teamMembers: updatedTeamMembers 
      });
      
      toast.success('Team member added successfully!');
      setTeamDialogOpen(false);
      setNewTeamMember({ name: '', role: '', contractValue: 0, initialPaid: 0, contractDescription: '', assignmentDate: '', contractFile: null, contractFileName: '', subWorkIds: [] });
      fetchData();
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error('Failed to add team member');
    }
  };

  const handleSelectTeamMember = (memberName) => {
    const member = allTeamMembers.find(m => m.name === memberName);
    if (member) {
      setNewTeamMember({
        ...newTeamMember,
        name: member.name,
        role: member.role
      });
    }
  };

  const handleEditProject = () => {
    setProjectEditForm({
      contractValue: project.contractValue,
      initialPaid: project.initialPaid || 0,
      clientId: project.clientId || '',
      clientName: project.clientName || '',
      subWorks: project.subWorks ? [...project.subWorks] : [],
      financeBook: project.financeBook || 'freelance'
    });
    setProjectEditDialogOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        contractValue: parseFloat(projectEditForm.contractValue),
        initialPaid: parseFloat(projectEditForm.initialPaid),
        clientId: projectEditForm.clientId || null,
        clientName: projectEditForm.clientName || null,
        subWorks: projectEditForm.subWorks,
        financeBook: projectEditForm.financeBook
      };
      
      await axios.put(`${API}/projects/${id}`, updateData);
      
      toast.success('Project updated successfully!');
      setProjectEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const handleEditTeamMember = (index) => {
    const member = project.teamMembers[index];
    setTeamMemberEditForm({
      contractValue: member.contractValue,
      initialPaid: member.initialPaid || 0,
      contractDescription: member.contractDescription || '',
      assignmentDate: member.assignmentDate || '',
      subWorkIds: member.subWorkIds || []
    });
    setEditingTeamMemberIndex(index);
    setTeamMemberEditDialogOpen(true);
  };

  const handleUpdateTeamMember = async (e) => {
    e.preventDefault();
    
    if (editingTeamMemberIndex === null) return;
    
    try {
      const updatedTeamMembers = [...project.teamMembers];
      updatedTeamMembers[editingTeamMemberIndex] = {
        ...updatedTeamMembers[editingTeamMemberIndex],
        contractValue: parseFloat(teamMemberEditForm.contractValue),
        initialPaid: parseFloat(teamMemberEditForm.initialPaid),
        contractDescription: teamMemberEditForm.contractDescription,
        assignmentDate: teamMemberEditForm.assignmentDate,
        subWorkIds: teamMemberEditForm.subWorkIds
      };

      await axios.put(`${API}/projects/${id}`, {
        teamMembers: updatedTeamMembers
      });
      
      toast.success('Team member updated successfully!');
      setTeamMemberEditDialogOpen(false);
      setEditingTeamMemberIndex(null);
      fetchData();
    } catch (error) {
      console.error('Error updating team member:', error);
      toast.error('Failed to update team member');
    }
  };

  const getMilestoneProgress = (milestone) => {
    const progress = {
      'Schematic': 25,
      'DED': 50,
      'For Construction': 75,
      'Done': 100
    };
    return progress[milestone] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Project not found</div>
      </div>
    );
  }

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div className="min-h-screen p-6" data-testid="project-detail-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.1</span>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Work Sans, sans-serif'}}>{project.name}</h1>
              <div className="flex items-center gap-3">
                <p className="text-slate-600">Contract Value: {formatCurrency(project.contractValue)}</p>
                {project.contractFile && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => {
                        const win = window.open('', '_blank');
                        if (project.contractFile.startsWith('data:application/pdf')) {
                          win.document.write(`<iframe src="${project.contractFile}" style="width:100%;height:100%;border:none;"></iframe>`);
                        } else {
                          win.document.write(`<img src="${project.contractFile}" style="max-width:100%;height:auto;" />`);
                        }
                      }}
                      data-testid="view-project-contract"
                    >
                      <Eye className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = project.contractFile;
                        link.download = project.contractFileName || 'project-contract';
                        link.click();
                      }}
                      data-testid="download-project-contract"
                    >
                      <Download className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Milestone Progress */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Project Progress</CardTitle>
                  <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="update-milestone-btn">Update Milestone</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Update Project Milestone</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        {milestones.map(milestone => (
                          <Button
                            key={milestone}
                            variant={project.milestone === milestone ? 'default' : 'outline'}
                            className="w-full"
                            onClick={() => updateMilestone(milestone)}
                            data-testid={`milestone-${milestone}`}
                          >
                            {milestone}
                          </Button>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{project.milestone}</span>
                    <span className="text-slate-600">{getMilestoneProgress(project.milestone)}%</span>
                  </div>
                  <Progress value={getMilestoneProgress(project.milestone)} className="h-3" />
                  <div className="flex justify-between text-xs text-slate-500 mt-3">
                    {milestones.map(m => (
                      <span key={m}>{m}</span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tasks */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Tasks ({pendingTasks.length} pending)</CardTitle>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-task-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Task
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Task</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleTaskSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="description">Task Description</Label>
                          <Input
                            id="description"
                            data-testid="task-description-input"
                            value={taskForm.description}
                            onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                            placeholder="Enter task description"
                          />
                        </div>
                        <div>
                          <Label htmlFor="deadline">Deadline</Label>
                          <Input
                            id="deadline"
                            type="date"
                            data-testid="task-deadline-input"
                            value={taskForm.deadline}
                            onChange={(e) => setTaskForm({...taskForm, deadline: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Assign Team (Max 3 people)</Label>
                          <p className="text-xs text-slate-600 mb-2">Add people in work process order</p>
                          <Select onValueChange={addPersonToTask} value="">
                            <SelectTrigger data-testid="task-person-select">
                              <SelectValue placeholder="+ Add team member" />
                            </SelectTrigger>
                            <SelectContent>
                              {project.teamMembers
                                ?.filter(m => !taskForm.assignedTo.find(p => p.name === m.name))
                                .map(member => (
                                  <SelectItem key={member.name} value={member.name}>
                                    {member.name} - {member.role}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          
                          {taskForm.assignedTo.length > 0 && (
                            <div className="mt-3 p-3 border rounded-lg bg-slate-50 space-y-3">
                              <p className="text-sm font-medium">Assigned Team:</p>
                              {taskForm.assignedTo.map((person, index) => {
                                const deadlines = personDeadlines[person.name] || [];
                                const blockedDates = deadlines.map(d => new Date(d.date));
                                
                                return (
                                <div key={person.name} className="p-3 bg-white rounded border space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium">{person.name}</span>
                                    <button type="button" onClick={() => removePersonFromTask(person.name)} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                                  </div>
                                  <Input
                                    value={person.subDescription}
                                    onChange={(e) => updatePersonInTask(index, 'subDescription', e.target.value)}
                                    placeholder="Job description for this person (required)"
                                    className="text-sm h-8"
                                  />
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button variant="outline" size="sm" className="w-full justify-start text-left text-sm h-8">
                                        <Calendar className="w-3 h-3 mr-2" />
                                        {person.deadline || 'Pick deadline (required)'}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <div className="p-2 text-xs text-slate-500 border-b bg-slate-50">
                                        <span className="inline-block w-3 h-3 bg-red-500 rounded mr-1 align-middle" /> = Existing deadline
                                      </div>
                                      <CalendarPicker
                                        mode="single"
                                        selected={person.deadline ? new Date(person.deadline) : undefined}
                                        onSelect={(date) => {
                                          if (date) {
                                            const formatted = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
                                            updatePersonInTask(index, 'deadline', formatted);
                                          }
                                        }}
                                        modifiers={{ blocked: blockedDates }}
                                        modifiersStyles={{
                                          blocked: { backgroundColor: '#ef4444', color: 'white', fontWeight: 'bold', borderRadius: '50%' }
                                        }}
                                        className="rounded-md border-0"
                                        classNames={{
                                          day: "h-9 w-9 p-0 font-normal text-sm hover:bg-slate-100 rounded-full flex items-center justify-center",
                                          head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                                          cell: "relative p-0 text-center text-sm",
                                          row: "flex w-full mt-1",
                                        }}
                                        components={{
                                          DayContent: ({ date }) => {
                                            const dayDeadlines = deadlines.filter(d => {
                                              const dd = new Date(d.date);
                                              return dd.getDate() === date.getDate() && dd.getMonth() === date.getMonth() && dd.getFullYear() === date.getFullYear();
                                            });
                                            return (
                                              <div title={dayDeadlines.length > 0 ? dayDeadlines.map(d => `${d.project}: ${d.task}`).join('\n') : ''}>
                                                {date.getDate()}
                                              </div>
                                            );
                                          }
                                        }}
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  {deadlines.length > 0 && (
                                    <p className="text-[10px] text-slate-400">{deadlines.length} existing deadline(s) marked in red</p>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="submit-task-btn">Create Task</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <div className="text-center py-8 text-slate-600">
                    No tasks yet. Create your first task!
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.map(task => {
                      const assignedPeople = (task.assignedTo || [{name: task.picName, completed: task.status === 'completed'}]).map(p => ({
                        name: p.name || p,
                        completed: p.completed || false,
                        subDescription: p.subDescription || '',
                        deadline: p.deadline || ''
                      }));
                      const completedCount = assignedPeople.filter(p => p.completed).length;
                      const totalCount = assignedPeople.length;
                      
                      return (
                      <div 
                        key={task.id} 
                        className={`p-4 border rounded-lg ${task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}
                        data-testid={`task-${task.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-600' : 'text-slate-800'}`}>
                                {task.description}
                              </p>
                              {totalCount > 1 && (
                                <Badge variant="outline" className="text-xs">
                                  {completedCount}/{totalCount} done
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {task.deadline}
                              </span>
                            </div>
                            
                            {/* Workflow with individual checkboxes */}
                            <div className="ml-2 space-y-2">
                              <p className="text-xs text-slate-500 mb-1">Workflow:</p>
                              <div className="space-y-1">
                                {assignedPeople.map((person, index) => (
                                  <div key={person.name} className="flex items-center gap-2">
                                    <button
                                      onClick={() => togglePersonCompletion(task.id, person.name)}
                                      className="flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-slate-50 transition-colors flex-shrink-0"
                                      data-testid={`task-person-${task.id}-${index}`}
                                    >
                                      <CheckCircle 
                                        className={`w-4 h-4 ${person.completed ? 'text-green-600 fill-green-600' : 'text-slate-400'}`}
                                      />
                                      <span className={`text-sm ${person.completed ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                                        {person.name}
                                      </span>
                                    </button>
                                    {person.subDescription && (
                                      <span className="text-xs text-slate-500 italic">- {person.subDescription}</span>
                                    )}
                                    {person.deadline && (
                                      <span className="text-xs text-slate-400 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" /> {person.deadline}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditTask(task)}
                              data-testid={`edit-task-${task.id}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTaskClick(task.id)}
                              data-testid={`delete-task-${task.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Summary */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Project Summary</CardTitle>
                  <Button variant="outline" size="sm" onClick={handleEditProject} data-testid="edit-project-btn">
                    Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600">Contract Value</p>
                  <p className="text-xl font-bold text-slate-800 break-words">{formatCurrency(project.contractValue)}</p>
                </div>
                <div className="space-y-2 border-t pt-3">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-slate-500 italic">Initial Paid (before integration)</p>
                    <p className="text-sm font-medium text-slate-600 break-words">{formatCurrency(project.initialPaid || 0)}</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-slate-500">Paid from Transactions</p>
                    <p className="text-sm font-medium text-slate-600 break-words">{formatCurrency(project.amountPaid || 0)}</p>
                  </div>
                  <div className="border-t pt-2">
                    <p className="text-sm text-slate-600">Total Paid</p>
                    <p className="text-xl font-bold text-green-600 break-words">{formatCurrency((project.initialPaid || 0) + (project.amountPaid || 0))}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Remaining</p>
                  <p className="text-xl font-bold text-orange-600 break-words">{formatCurrency(project.contractValue - ((project.initialPaid || 0) + (project.amountPaid || 0)))}</p>
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            {project.clientName && (
              <Card className="bg-white shadow-sm">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-medium text-slate-800">{project.clientName}</p>
                </CardContent>
              </Card>
            )}

            {/* Sub-Works */}
            {project.subWorks && project.subWorks.length > 0 && (
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Sub-Works / Bangunan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {project.subWorks.map((sw, i) => (
                      <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                        <span>{sw.name}</span>
                        <span className="font-medium">{formatCurrency(sw.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t font-semibold text-sm">
                      <span>Total</span>
                      <span>{formatCurrency(project.subWorks.reduce((s, w) => s + (w.price || 0), 0))}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Financial Summary */}
            <Card className="bg-blue-50 border-blue-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-blue-900">Team Contracts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-blue-700">Total Team Contract</p>
                  <p className="text-xl font-bold text-blue-900 break-words">
                    {formatCurrency(project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0)}
                  </p>
                </div>
                <div className="border-t border-blue-200 pt-3">
                  <p className="text-sm text-blue-700">Total Team Paid</p>
                  <p className="text-xl font-bold text-green-700 break-words">
                    {formatCurrency(project.teamMembers?.reduce((sum, member) => sum + ((member.initialPaid || 0) + (member.amountPaid || 0)), 0) || 0)}
                  </p>
                </div>
                <div className="border-t border-blue-200 pt-3">
                  <p className="text-sm text-blue-700">Team Remaining</p>
                  <p className="text-xl font-bold text-orange-700 break-words">
                    {formatCurrency(
                      (project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0) -
                      (project.teamMembers?.reduce((sum, member) => sum + ((member.initialPaid || 0) + (member.amountPaid || 0)), 0) || 0)
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Financial Health Indicator */}
            <Card className="bg-purple-50 border-purple-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-purple-900">Financial Health</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-purple-700">Project Margin</p>
                  <p className="text-xl font-bold text-purple-900 break-words">
                    {formatCurrency(
                      project.contractValue - (project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0)
                    )}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    {project.contractValue > 0 
                      ? `${(((project.contractValue - (project.teamMembers?.reduce((sum, member) => sum + (member.contractValue || 0), 0) || 0)) / project.contractValue) * 100).toFixed(1)}% margin`
                      : '0% margin'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Edit Project Dialog */}
            <Dialog open={projectEditDialogOpen} onOpenChange={setProjectEditDialogOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Project Details</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateProject} className="space-y-4">
                  {/* Client */}
                  <div>
                    <Label>Client</Label>
                    <Select
                      value={projectEditForm.clientId}
                      onValueChange={(val) => {
                        const client = allClients.find(c => c.id === val);
                        setProjectEditForm({
                          ...projectEditForm,
                          clientId: val,
                          clientName: client ? `${client.name} - ${client.company}` : ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="edit-project-client-select">
                        <SelectValue placeholder="Select client (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {allClients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name} - {c.company}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Finance Book */}
                  <div>
                    <Label>Finance Book</Label>
                    <Select
                      value={projectEditForm.financeBook}
                      onValueChange={(val) => setProjectEditForm({...projectEditForm, financeBook: val})}
                    >
                      <SelectTrigger data-testid="edit-project-book-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freelance">Freelance (BCA - Johan)</SelectItem>
                        <SelectItem value="company">Company (BCA - Cremona)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit-contract">Contract Value (Rp)</Label>
                    <Input
                      id="edit-contract"
                      type="number"
                      data-testid="edit-project-contract-input"
                      value={projectEditForm.contractValue}
                      onChange={(e) => setProjectEditForm({...projectEditForm, contractValue: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-initial-paid">Initial Paid (Rp)</Label>
                    <Input
                      id="edit-initial-paid"
                      type="number"
                      data-testid="edit-project-initial-paid-input"
                      value={projectEditForm.initialPaid}
                      onChange={(e) => setProjectEditForm({...projectEditForm, initialPaid: e.target.value})}
                    />
                    <p className="text-xs text-slate-500 italic mt-1">(Paid before project integrated to program)</p>
                  </div>
                  {/* Sub-Works */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Sub-Works / Bangunan</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setProjectEditForm({
                          ...projectEditForm,
                          subWorks: [...projectEditForm.subWorks, { id: crypto.randomUUID(), name: '', price: 0 }]
                        })}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {projectEditForm.subWorks.length > 0 && (
                      <div className="space-y-2">
                        {projectEditForm.subWorks.map((sw, i) => (
                          <div key={sw.id || i} className="flex gap-2 items-center">
                            <Input
                              value={sw.name}
                              onChange={(e) => {
                                const updated = [...projectEditForm.subWorks];
                                updated[i] = { ...updated[i], name: e.target.value };
                                setProjectEditForm({ ...projectEditForm, subWorks: updated });
                              }}
                              placeholder="Sub-work name"
                              className="flex-1 text-sm"
                            />
                            <Input
                              type="number"
                              value={sw.price || ''}
                              onChange={(e) => {
                                const updated = [...projectEditForm.subWorks];
                                updated[i] = { ...updated[i], price: parseFloat(e.target.value) || 0 };
                                setProjectEditForm({ ...projectEditForm, subWorks: updated });
                              }}
                              placeholder="Price"
                              className="w-32 text-sm"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setProjectEditForm({
                                  ...projectEditForm,
                                  subWorks: projectEditForm.subWorks.filter((_, idx) => idx !== i)
                                });
                              }}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setProjectEditDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-project-edit-btn">Update</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Team Members */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Team Members ({project.teamMembers?.length || 0})</CardTitle>
                  <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-team-member-project-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Member
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Team Member to Project</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddTeamMember} className="space-y-4">
                        <div>
                          <Label htmlFor="team-member">Select Team Member</Label>
                          <Select onValueChange={handleSelectTeamMember} value={newTeamMember.name}>
                            <SelectTrigger data-testid="project-team-member-select">
                              <SelectValue placeholder="Select team member" />
                            </SelectTrigger>
                            <SelectContent>
                              {allTeamMembers
                                .filter(tm => !project.teamMembers?.find(pm => pm.name === tm.name))
                                .map(member => (
                                  <SelectItem key={member.id} value={member.name}>
                                    {member.name} - {member.role}
                                  </SelectItem>
                                ))}
                              {allTeamMembers.filter(tm => !project.teamMembers?.find(pm => pm.name === tm.name)).length === 0 && (
                                <div className="p-2 text-sm text-slate-500 text-center">
                                  All team members already added
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        {newTeamMember.role !== 'Principal Engineer' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="member-contract">Contract Value (Rp)</Label>
                            <Input
                              id="member-contract"
                              type="number"
                              data-testid="member-contract-input"
                              value={newTeamMember.contractValue}
                              onChange={(e) => setNewTeamMember({...newTeamMember, contractValue: e.target.value})}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label htmlFor="member-initial-paid">Initial Paid (Rp)</Label>
                            <Input
                              id="member-initial-paid"
                              type="number"
                              data-testid="member-initial-paid-input"
                              value={newTeamMember.initialPaid}
                              onChange={(e) => setNewTeamMember({...newTeamMember, initialPaid: e.target.value})}
                              placeholder="0"
                            />
                            <p className="text-xs text-slate-500 italic mt-1">(Before integration)</p>
                          </div>
                        </div>
                        )}
                        <div>
                          <Label>Tanggal Penugasan (Assignment Date)</Label>
                          <Input
                            type="date"
                            data-testid="member-assignment-date-input"
                            value={newTeamMember.assignmentDate || ''}
                            onChange={(e) => setNewTeamMember({...newTeamMember, assignmentDate: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Work Description / Contract Summary</Label>
                          <Textarea
                            data-testid="member-contract-desc-input"
                            value={newTeamMember.contractDescription || ''}
                            onChange={(e) => setNewTeamMember({...newTeamMember, contractDescription: e.target.value})}
                            placeholder="Describe the work scope..."
                            className="min-h-[60px] text-sm"
                          />
                        </div>
                        <div>
                          <Label>Upload Contract (PDF/JPG)</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <label className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded cursor-pointer transition-colors">
                              <Upload className="w-4 h-4" />
                              {newTeamMember.contractFileName || 'Choose file'}
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    setNewTeamMember({...newTeamMember, contractFile: reader.result, contractFileName: file.name});
                                  };
                                  reader.readAsDataURL(file);
                                }}
                              />
                            </label>
                          </div>
                        </div>
                        {/* Sub-Work Assignment */}
                        {project.subWorks && project.subWorks.length > 0 && (
                          <div>
                            <Label>Assign Sub-Works</Label>
                            <div className="space-y-2 mt-2">
                              {project.subWorks.map(sw => (
                                <label key={sw.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm cursor-pointer hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={newTeamMember.subWorkIds?.includes(sw.id) || false}
                                    onChange={(e) => {
                                      const ids = newTeamMember.subWorkIds || [];
                                      setNewTeamMember({
                                        ...newTeamMember,
                                        subWorkIds: e.target.checked ? [...ids, sw.id] : ids.filter(i => i !== sw.id)
                                      });
                                    }}
                                    className="rounded"
                                  />
                                  <span>{sw.name}</span>
                                  <span className="text-slate-400 ml-auto">{formatCurrency(sw.price)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setTeamDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="submit-team-member-btn">Add to Project</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {project.teamMembers?.length === 0 ? (
                  <p className="text-sm text-slate-600">No team members assigned</p>
                ) : (
                  <div className="space-y-3">
                    {project.teamMembers?.map((member, index) => {
                      // Find team member ID from allTeamMembers list
                      const teamMemberData = allTeamMembers.find(tm => tm.name === member.name);
                      const memberId = teamMemberData?.id;
                      
                      return (
                      <div 
                        key={index} 
                        className="p-3 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => {
                          if (memberId) {
                            navigate(`/team/${memberId}`);
                          }
                        }}
                        data-testid={`team-member-${index}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-slate-800">{member.name}</p>
                            <p className="text-sm text-slate-600">{member.role}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {tasks.filter(t => {
                                if (t.status !== 'pending') return false;
                                const assignedTo = t.assignedTo || [];
                                return assignedTo.some(a => a.name === member.name && !a.completed);
                              }).length} tasks
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTeamMember(index);
                              }}
                              data-testid={`edit-team-member-${index}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTeamMemberClick(index);
                              }}
                              data-testid={`delete-team-member-${index}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Contract:</span>
                            <span className="font-medium">{formatCurrency(member.contractValue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-slate-500 italic">Initial Paid:</span>
                            <span className="text-xs text-slate-600">{formatCurrency(member.initialPaid || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-slate-500">From Transactions:</span>
                            <span className="text-xs text-slate-600">{formatCurrency(member.amountPaid || 0)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1 mt-1">
                            <span className="text-slate-600">Total Paid:</span>
                            <span className="font-medium text-green-600">{formatCurrency((member.initialPaid || 0) + (member.amountPaid || 0))}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Remaining:</span>
                            <span className="font-medium text-orange-600">{formatCurrency(member.contractValue - ((member.initialPaid || 0) + (member.amountPaid || 0)))}</span>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Team Member Dialog */}
            <Dialog open={teamMemberEditDialogOpen} onOpenChange={setTeamMemberEditDialogOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Team Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateTeamMember} className="space-y-4">
                  {editingTeamMemberIndex !== null && project.teamMembers && (
                    <div className="mb-4 p-3 bg-slate-50 rounded">
                      <p className="font-medium">{project.teamMembers[editingTeamMemberIndex]?.name}</p>
                      <p className="text-sm text-slate-600">{project.teamMembers[editingTeamMemberIndex]?.role}</p>
                    </div>
                  )}
                  {/* Hide contract fields for Principal Engineer */}
                  {editingTeamMemberIndex !== null && project.teamMembers?.[editingTeamMemberIndex]?.role !== 'Principal Engineer' && (
                    <>
                      <div>
                        <Label htmlFor="edit-member-contract">Contract Value (Rp)</Label>
                        <Input
                          id="edit-member-contract"
                          type="number"
                          data-testid="edit-member-contract-input"
                          value={teamMemberEditForm.contractValue}
                          onChange={(e) => setTeamMemberEditForm({...teamMemberEditForm, contractValue: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-member-initial-paid">Initial Paid (Rp)</Label>
                        <Input
                          id="edit-member-initial-paid"
                          type="number"
                          data-testid="edit-member-initial-paid-input"
                          value={teamMemberEditForm.initialPaid}
                          onChange={(e) => setTeamMemberEditForm({...teamMemberEditForm, initialPaid: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 italic mt-1">(Paid before project integrated to program)</p>
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Tanggal Penugasan (Assignment Date)</Label>
                    <Input
                      type="date"
                      value={teamMemberEditForm.assignmentDate || ''}
                      onChange={(e) => setTeamMemberEditForm({...teamMemberEditForm, assignmentDate: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Scope of Work / Contract Summary</Label>
                    <Textarea
                      value={teamMemberEditForm.contractDescription || ''}
                      onChange={(e) => setTeamMemberEditForm({...teamMemberEditForm, contractDescription: e.target.value})}
                      placeholder="Describe the work scope..."
                      className="min-h-[60px] text-sm"
                    />
                  </div>
                  {/* Sub-Work Assignment */}
                  {project.subWorks && project.subWorks.length > 0 && (
                    <div>
                      <Label>Assigned Sub-Works</Label>
                      <div className="space-y-2 mt-2">
                        {project.subWorks.map(sw => (
                          <label key={sw.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm cursor-pointer hover:bg-slate-100">
                            <input
                              type="checkbox"
                              checked={teamMemberEditForm.subWorkIds?.includes(sw.id) || false}
                              onChange={(e) => {
                                const ids = teamMemberEditForm.subWorkIds || [];
                                setTeamMemberEditForm({
                                  ...teamMemberEditForm,
                                  subWorkIds: e.target.checked
                                    ? [...ids, sw.id]
                                    : ids.filter(id => id !== sw.id)
                                });
                              }}
                              className="rounded"
                            />
                            <span>{sw.name}</span>
                            <span className="text-slate-400 ml-auto">{formatCurrency(sw.price)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTeamMemberEditDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-member-edit-btn">Update</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Delete Task Confirmation Dialog */}
            <DeleteConfirmDialog
              open={deleteTaskDialogOpen}
              onOpenChange={setDeleteTaskDialogOpen}
              onConfirm={confirmDeleteTask}
              title="Delete Task"
              description="Are you sure you want to delete this task? This action cannot be undone."
            />

            {/* Delete Team Member Confirmation Dialog */}
            <DeleteConfirmDialog
              open={deleteTeamMemberDialogOpen}
              onOpenChange={setDeleteTeamMemberDialogOpen}
              onConfirm={confirmDeleteTeamMember}
              title="Remove Team Member"
              description="Are you sure you want to remove this team member from the project? This action cannot be undone."
            />

            {/* Edit Task Dialog */}
            <Dialog open={editTaskDialogOpen} onOpenChange={setEditTaskDialogOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
                {editingTask && (
                  <form onSubmit={handleEditTaskSubmit} className="space-y-4">
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={editingTask.description}
                        onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                        data-testid="edit-task-description"
                      />
                    </div>
                    <div>
                      <Label>Main Deadline</Label>
                      <Input
                        type="date"
                        value={editingTask.deadline}
                        onChange={(e) => setEditingTask({...editingTask, deadline: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Assigned Team</Label>
                      <div className="space-y-3 mt-2">
                        {editingTask.assignedTo.map((person, i) => (
                          <div key={person.name} className="p-3 bg-slate-50 rounded border space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{person.name}</span>
                              <button
                                type="button"
                                onClick={() => setEditingTask({
                                  ...editingTask,
                                  assignedTo: editingTask.assignedTo.filter((_, idx) => idx !== i)
                                })}
                                className="text-red-600 text-xs"
                              >Remove</button>
                            </div>
                            <Input
                              value={person.subDescription}
                              onChange={(e) => {
                                const updated = [...editingTask.assignedTo];
                                updated[i] = {...updated[i], subDescription: e.target.value};
                                setEditingTask({...editingTask, assignedTo: updated});
                              }}
                              placeholder="Job description for this person"
                              className="text-sm h-8"
                            />
                            <Input
                              type="date"
                              value={person.deadline}
                              onChange={(e) => {
                                const updated = [...editingTask.assignedTo];
                                updated[i] = {...updated[i], deadline: e.target.value};
                                setEditingTask({...editingTask, assignedTo: updated});
                              }}
                              className="text-sm h-8"
                            />
                          </div>
                        ))}
                        {/* Add more people */}
                        <Select onValueChange={(name) => {
                          if (!editingTask.assignedTo.find(p => p.name === name)) {
                            setEditingTask({
                              ...editingTask,
                              assignedTo: [...editingTask.assignedTo, {name, subDescription: '', deadline: ''}]
                            });
                          }
                        }}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="+ Add person" /></SelectTrigger>
                          <SelectContent>
                            {project.teamMembers?.filter(tm => !editingTask.assignedTo.find(p => p.name === tm.name)).map(tm => (
                              <SelectItem key={tm.name} value={tm.name}>{tm.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setEditTaskDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" data-testid="submit-edit-task">Update Task</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;